import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { SunMedium, Repeat2, Image as ImageIcon, Check, Wand2 } from "lucide-react";

const dimsOf = (it) => { const d = (it && it.dimensioni_mm) || {}; return (d.larghezza && d.lunghezza) ? `${d.larghezza}×${d.lunghezza}` : null; };

// Selettore pannello per l'Area Free: sceglie tra i moduli esposti in Vetrina (nessun prezzo).
export function FreePanelPicker({ selfToken, currentModule, roof, onChanged, className = "", rete = false, required = false }) {
  const [open, setOpen] = useState(false);
  const [panels, setPanels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/public/vetrina").then((r) => {
      const slot = (r.data.slots || []).find((s) => s.slot === "pannello");
      setPanels(slot?.items || []);
    }).catch(() => setPanels([])).finally(() => setLoading(false));
  }, []);

  const layoutLocked = !!((roof?.panel_layout && roof.panel_layout.enabled) || (roof?.flat_layout && roof.flat_layout.compatible));
  const curL = currentModule?.larghezza_mm, curH = currentModule?.lunghezza_mm;
  const curCode = currentModule?.model || currentModule?.codice;
  const isAuto = (currentModule?.source === "catalogo");
  // Rete: pannello "acquisito" se scelto a mano (vetrina) o fornito automaticamente da rete.torri.solar.
  // "acquisito" = scelta esplicita (chosen) o pannello fornito automaticamente dalla rete.
  const reteChosen = currentModule?.chosen === true || (currentModule?.source === "rete.torri.solar" && !!currentModule?.provided);
  const reteNeedsChoice = (required || rete) && !reteChosen;

  const options = useMemo(() => {
    if (layoutLocked && curL && curH) {
      return panels.filter((it) => { const d = it.dimensioni_mm || {}; return Number(d.larghezza) === Number(curL) && Number(d.lunghezza) === Number(curH); });
    }
    return panels;
  }, [panels, layoutLocked, curL, curH]);

  if (loading || panels.length < 1) return null; // niente scelta se la Vetrina non ha pannelli

  const choose = async (codice) => {
    setSaving(true);
    try {
      const { data } = await api.post(`/public/self/${selfToken}/listino-panel`, { codice });
      toast.success("Pannello aggiornato");
      onChanged?.(data);
      setOpen(false);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <div className={`rounded-lg border p-3 ${reteNeedsChoice ? "border-red-500/70 bg-red-500/10" : "border-primary/30 bg-primary/[0.04]"} ${className}`} data-testid="free-panel-picker">
      <div className="flex items-center gap-3">
        <SunMedium className={`h-5 w-5 shrink-0 ${reteNeedsChoice ? "text-red-600" : "text-primary"}`} strokeWidth={1.5} />
        <div className="min-w-0 flex-1">
          <div className={`text-[10px] uppercase tracking-wide font-semibold ${reteNeedsChoice ? "text-red-600" : "text-primary"}`}>Scegli il tuo pannello{(required || rete) ? " (obbligatorio)" : ""}</div>
          <div className="text-sm font-semibold truncate">{reteNeedsChoice ? <span className="text-red-600">Nessun pannello selezionato</span> : (isAuto ? "Automatico" : (curCode || "Pannello consigliato"))}{!isAuto && !reteNeedsChoice && currentModule?.potenza_wp ? <span className="text-muted-foreground font-normal"> · {Math.round(currentModule.potenza_wp)} Wp</span> : null}</div>
        </div>
        <Button variant={reteNeedsChoice ? "default" : "outline"} size="sm" className="gap-1.5 shrink-0" onClick={() => setOpen(true)} data-testid="free-panel-change-btn"><Repeat2 className="h-4 w-4" /> {reteNeedsChoice ? "Scegli" : "Cambia"}</Button>
      </div>
      {reteNeedsChoice && (
        <div className="mt-2.5 flex items-start gap-2 rounded-md border border-red-500/50 bg-red-500/5 px-2.5 py-2 text-xs text-red-700 dark:text-red-400" data-testid="free-panel-required-alert">
          <Repeat2 className="h-4 w-4 shrink-0 mt-0.5" />
          <span><b>Scelta del pannello obbligatoria.</b> Seleziona un pannello per proseguire: non è previsto alcun pannello di default e il layout resterà bloccato finché non scegli.</span>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Scegli il pannello</DialogTitle></DialogHeader>
          {layoutLocked && curL && curH ? (
            <div className="text-xs rounded-md border border-amber-400/50 bg-amber-50 text-amber-800 px-3 py-2">Hai già un layout: puoi cambiare solo tra moduli con le <b>stesse dimensioni</b> ({curL}×{curH} mm).</div>
          ) : null}
          <div className="max-h-[55vh] overflow-y-auto -mx-1 px-1 space-y-1.5 mt-1">
            {!(required || rete) && (
            <button onClick={() => choose("__auto__")} disabled={saving} className={`w-full flex items-center gap-3 rounded-lg border p-2.5 text-left transition-colors disabled:opacity-50 ${isAuto ? "border-primary bg-primary/10" : "border-border hover:border-primary/50 hover:bg-secondary/40"}`} data-testid="free-panel-auto">
              <div className="w-14 h-14 rounded bg-primary/10 shrink-0 flex items-center justify-center"><Wand2 className="h-6 w-6 text-primary" strokeWidth={1.5} /></div>
              <div className="min-w-0 flex-1"><div className="text-sm font-semibold">Automatico</div><div className="text-xs text-muted-foreground line-clamp-2">Lascia scegliere al sistema il pannello consigliato.</div></div>
              {isAuto ? <Check className="h-5 w-5 text-primary shrink-0" /> : null}
            </button>
            )}
            {options.length === 0 ? <div className="py-6 text-center text-sm text-muted-foreground">Nessun altro modulo compatibile.</div>
             : options.map((it) => {
              const selected = !isAuto && curCode && String(curCode) === String(it.codice);
              return (
              <button key={it.codice} onClick={() => choose(it.codice)} disabled={saving} className={`w-full flex items-center gap-3 rounded-lg border p-2.5 text-left transition-colors disabled:opacity-50 ${selected ? "border-primary bg-primary/10" : "border-border hover:border-primary/50 hover:bg-secondary/40"}`} data-testid={`free-panel-option-${it.codice}`}>
                <div className="w-14 h-14 rounded overflow-hidden border border-border bg-secondary/30 shrink-0 flex items-center justify-center">{(it.immagine_listino || it.immagine_url) ? <img src={it.immagine_listino || it.immagine_url} alt="" className="w-full h-full object-contain bg-white" /> : <ImageIcon className="h-5 w-5 text-muted-foreground/40" />}</div>
                <div className="min-w-0 flex-1"><div className="text-sm font-semibold truncate">{it.codice}</div><div className="text-xs text-muted-foreground line-clamp-2">{it.descrizione}</div><div className="text-[11px] text-muted-foreground mt-0.5">{it.potenza_wp ? `${it.potenza_wp} Wp` : ""}{dimsOf(it) ? ` · ${dimsOf(it)} mm` : ""}</div></div>
                {selected ? <Check className="h-5 w-5 text-primary shrink-0" /> : null}
              </button>
            );})}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
